<template>
  <v-card class="sd">
    <header class="sd__head">
      <div class="sd__head-text">
        <!-- El tipo va en la cabecera: gastos operativos y servicios
             aduanales se emiten por separado. Los de antes de separarlos
             (sin mes) mezclaban los dos y se rotulan aparte. -->
        <p class="sd__eyebrow">{{ eyebrow }}</p>
        <h2 class="sd__title">{{ data.number || '—' }}</h2>
        <p class="sd__scope">
          {{ data.clientName }}
          <template v-if="data.period"
            >· {{ mesDe(data.period) }}</template
          >
          <template v-else-if="data.periodFrom">
            · {{ dateOf(data.periodFrom) }} al {{ dateOf(data.periodTo) }}
          </template>
        </p>
      </div>
      <div class="sd__head-right">
        <span class="sd__badge" :class="badgeClass">{{ badgeText }}</span>
        <button
          type="button"
          class="sd__close"
          title="Cerrar"
          @click="$emit('close-statement')"
        >
          <v-icon small>close</v-icon>
        </button>
      </div>
    </header>

    <div class="sd__body">
      <p v-if="loading" class="sd__help">Cargando…</p>

      <template v-else>
        <!-- Totales -->
        <!-- Solo los conceptos del tipo; uno del formato anterior muestra
             los tres para que sumen el total. -->
        <div class="sd__totals">
          <div v-if="showTax" class="sd__total">
            <span class="sd__total-label">Impuestos adelantados</span>
            <strong class="sd__total-value">
              B/. {{ money(data.taxTotal) }}
            </strong>
          </div>
          <div v-if="showReimbursement" class="sd__total">
            <span class="sd__total-label">Otros gastos por su cuenta</span>
            <strong class="sd__total-value">
              B/. {{ money(data.reimbursementTotal) }}
            </strong>
            <span class="sd__total-hint"
              >notaría, APA, bomberos, almacenaje…</span
            >
          </div>
          <div v-if="showService" class="sd__total">
            <span class="sd__total-label">Servicios aduanales</span>
            <strong class="sd__total-value">
              B/. {{ money(data.serviceTotal) }}
            </strong>
          </div>
          <div class="sd__total sd__total--grand">
            <span class="sd__total-label">Total</span>
            <strong class="sd__total-value">B/. {{ money(data.total) }}</strong>
            <span v-if="saldo > 0 && data.paidAmount" class="sd__total-note">
              saldo B/. {{ money(saldo) }}
            </span>
          </div>
        </div>

        <!-- Cuadro de KPI: los datos ya procesados al emitir -->
        <section v-if="data.kpi" class="sd__section">
          <h3 class="sd__section-title">
            Indicadores del periodo
            <span class="sd__section-note">al momento de emitir</span>
          </h3>
          <div class="sd__kpis">
            <div class="sd__kpi">
              <span class="sd__kpi-label">Trámites</span>
              <strong class="sd__kpi-value">{{ data.kpi.tramites }}</strong>
            </div>
            <div class="sd__kpi">
              <span class="sd__kpi-label">Consolidados</span>
              <strong class="sd__kpi-value">{{ data.kpi.consolidados }}</strong>
            </div>
            <div class="sd__kpi">
              <span class="sd__kpi-label">CIF total (USD)</span>
              <strong class="sd__kpi-value">
                {{ money(data.kpi.cifTotal) }}
              </strong>
            </div>
            <div class="sd__kpi">
              <span class="sd__kpi-label">Prom. días en F&amp;D</span>
              <strong class="sd__kpi-value">
                {{ nullable(data.kpi.promDiasFd) }}
              </strong>
            </div>
            <div class="sd__kpi">
              <span class="sd__kpi-label">Antigüedad promedio</span>
              <strong class="sd__kpi-value">
                {{ nullable(data.kpi.promAntiguedad) }}
              </strong>
            </div>
            <div class="sd__kpi">
              <span class="sd__kpi-label">Reingresos</span>
              <strong class="sd__kpi-value">{{ data.kpi.reingresos }}</strong>
            </div>
          </div>

          <table v-if="antiguedad.length" class="sd__table sd__table--aging">
            <thead>
              <tr>
                <th>Antigüedad desde la aprobación</th>
                <th class="sd__right">Trámites</th>
                <th class="sd__right">Monto (B/.)</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="b in antiguedad" :key="b.bucket">
                <td>{{ b.bucket }}</td>
                <td class="sd__right">{{ b.n }}</td>
                <td class="sd__right sd__amount">{{ money(b.monto) }}</td>
              </tr>
            </tbody>
          </table>
        </section>

        <!-- Detalle por trámite -->
        <section class="sd__section">
          <h3 class="sd__section-title">Detalle por trámite</h3>
          <!-- Si no cabe, la tabla se desplaza a lo ancho dentro del diálogo
               con la barra a la vista; el Subtotal queda fijo a la derecha
               para que nunca se corte. -->
          <div class="sd__table-wrap">
            <table class="sd__table sd__table--lines">
              <thead>
                <tr>
                  <th class="sd__col-factura"># Factura</th>
                  <th># Control</th>
                  <th>N° Alegra</th>
                  <th>Fecha Alegra</th>
                  <th class="sd__right">CIF</th>
                  <th v-if="showTax" class="sd__right">Impuesto</th>
                  <th v-if="showReimbursement" class="sd__right">
                    Otros gastos
                  </th>
                  <th v-if="showService" class="sd__right">Servicios</th>
                  <th class="sd__right sd__sticky">Subtotal</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="inv in data.invoices" :key="inv.invoiceId">
                  <!-- "# Factura" son los números del cliente, uno por línea.
                     Un trámite consolidado cobra una vez; su consecutivo
                     interno va debajo como referencia, porque el cliente no
                     lo reconoce como factura. -->
                  <td class="sd__strong sd__col-factura">
                    <div
                      v-for="(n, i) in numerosDe(inv)"
                      :key="i"
                      class="sd__factura"
                    >
                      {{ n }}
                    </div>
                    <div v-if="inv.consolidated" class="sd__facturas">
                      Consolidado {{ consolidadoDe(inv) }} ·
                      {{ numerosDe(inv).length }} facturas
                      <template v-if="controlFdDe(inv)">
                        · Control F&amp;D: {{ controlFdDe(inv) }}
                      </template>
                    </div>
                    <div v-else-if="inv.internalNumber" class="sd__facturas">
                      Trámite {{ inv.internalNumber }}
                    </div>
                  </td>
                  <!-- # Control = número de la DECLARACIÓN. Se edita aquí y se
                     guarda en el trámite vivo (la fila padre en un
                     consolidado), que es lo que va al siguiente correo y al
                     Excel. El cliente lo ve como texto. -->
                  <td v-if="canEdit" class="sd__control">
                    <input
                      type="text"
                      class="sd__input sd__control-input"
                      placeholder="N° de declaración"
                      :value="controlValue(inv)"
                      :readonly="controlOf(inv.invoiceId).saving"
                      :class="{
                        'sd__control-input--error': controlOf(inv.invoiceId)
                          .error,
                      }"
                      aria-label="Número de declaración"
                      @input="onControlInput(inv, $event.target.value)"
                      @keyup.enter="$event.target.blur()"
                      @keyup.esc="cancelControl(inv, $event)"
                      @blur="saveControl(inv)"
                    />
                    <span
                      v-if="controlOf(inv.invoiceId).saving"
                      class="sd__control-status"
                    >
                      Guardando…
                    </span>
                    <span
                      v-else-if="controlOf(inv.invoiceId).error"
                      class="sd__control-error"
                    >
                      {{ controlOf(inv.invoiceId).error }}
                    </span>
                  </td>
                  <td v-else>{{ inv.controlNumber || '—' }}</td>
                  <!-- La factura Alegra del trámite: es la línea que el cliente
                     concilia (número, fecha y monto). -->
                  <td class="sd__strong sd__nowrap">
                    {{ inv.alegraNumber || '—' }}
                  </td>
                  <td class="sd__nowrap">{{ dateOf(inv.alegraDate) }}</td>
                  <td class="sd__right">{{ money(inv.cif) }}</td>
                  <td v-if="showTax" class="sd__right">
                    {{ conceptOf(inv, 'TAX') }}
                  </td>
                  <td v-if="showReimbursement" class="sd__right">
                    {{ conceptOf(inv, 'REIMBURSEMENT') }}
                  </td>
                  <td v-if="showService" class="sd__right">
                    {{ conceptOf(inv, 'SERVICE') }}
                  </td>
                  <td class="sd__right sd__amount sd__sticky">
                    {{ money(inv.subtotal) }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <p
            v-if="canEdit && data.invoices && data.invoices.length"
            class="sd__help sd__help--control"
          >
            # Control: número de la DECLARACIÓN. Se guarda en el trámite al
            salir del campo.
          </p>
        </section>

        <!-- Abonos: cada pago por separado, no un acumulado suelto. Es lo
             que permite explicar un cobro después y deshacer un abono mal
             registrado (un estado pagado no se puede anular). -->
        <section v-if="payments.length" class="sd__section">
          <h3 class="sd__section-title">
            Abonos
            <span class="sd__section-note">
              suman B/. {{ money(data.paidAmount) }} de {{ money(data.total) }}
            </span>
          </h3>
          <ul class="sd__docs">
            <li v-for="ab in payments" :key="ab.id" class="sd__doc">
              <v-icon small class="sd__doc-icon">payments</v-icon>
              <span class="sd__pay-amount">B/. {{ money(ab.amount) }}</span>
              <span class="sd__doc-date">{{ dateOf(ab.paidAt) }}</span>
              <span v-if="ab.createdBy" class="sd__doc-date">
                {{ ab.createdBy.firstName }} {{ ab.createdBy.lastName }}
              </span>
              <span v-if="ab.notes" class="sd__doc-date">{{ ab.notes }}</span>
              <v-tooltip bottom v-if="canEdit">
                <template slot="activator">
                  <button
                    class="sd__doc-del"
                    aria-label="Eliminar abono"
                    :disabled="saving"
                    @click="removePayment(ab)"
                  >
                    <v-icon small>delete_outline</v-icon>
                  </button>
                </template>
                <span>
                  Eliminar este abono: el saldo y el estado se recalculan
                </span>
              </v-tooltip>
            </li>
          </ul>
        </section>

        <!-- Bitácora de documentos -->
        <section class="sd__section">
          <h3 class="sd__section-title">
            Documentos
            <span class="sd__section-note">
              cualquier formato, se suman a los que ya hay
            </span>
          </h3>
          <ul v-if="data.documents && data.documents.length" class="sd__docs">
            <li v-for="doc in data.documents" :key="doc.id" class="sd__doc">
              <v-icon small class="sd__doc-icon">insert_drive_file</v-icon>
              <a
                :href="fileUrl(doc.filePath)"
                target="_blank"
                rel="noopener"
                class="sd__doc-name"
              >
                {{ doc.originalName || doc.filePath }}
              </a>
              <span class="sd__doc-date">{{ dateOf(doc.createdAt) }}</span>
              <v-tooltip bottom v-if="canEdit">
                <template slot="activator">
                  <button
                    class="sd__doc-del"
                    aria-label="Quitar documento"
                    @click="removeDoc(doc)"
                  >
                    <v-icon small>delete_outline</v-icon>
                  </button>
                </template>
                <span>Quitar este documento del estado de cuenta</span>
              </v-tooltip>
            </li>
          </ul>
          <p v-else class="sd__help">
            Todavía no se ha adjuntado ningún archivo.
          </p>
        </section>

        <p v-if="notes" class="sd__notes">{{ notes }}</p>
        <p v-if="data.sentAt" class="sd__help">
          Último envío al cliente: {{ dateTimeOf(data.sentAt) }}
        </p>
        <p v-if="error" class="sd__error">{{ error }}</p>

        <input
          ref="file"
          type="file"
          multiple
          style="display: none"
          @change="upload"
        />
      </template>
    </div>

    <footer v-if="!loading" class="sd__foot">
      <v-tooltip top>
        <template slot="activator">
          <button
            class="sd__btn sd__btn--ghost"
            :disabled="downloading"
            @click="download"
          >
            <v-icon small class="sd__btn-icon">download</v-icon>
            {{ downloading ? 'Preparando…' : 'Descargar' }}
          </button>
        </template>
        <span>
          Zip de este estado de cuenta: Excel con el detalle y el cuadro de KPI,
          más los documentos adjuntos
        </span>
      </v-tooltip>
      <template v-if="canEdit">
        <!-- Un anulado se conserva para auditar: ya no se anula, adjunta,
             envía ni cobra (el backend también lo rechaza). -->
        <label class="sd__notify" :class="{ 'sd__notify--off': isVoid }">
          <input v-model="notifyClient" type="checkbox" :disabled="isVoid" />
          Avisar al cliente al adjuntar
        </label>
        <v-tooltip top>
          <template slot="activator">
            <button
              class="sd__btn sd__btn--ghost"
              :disabled="voiding || isVoid || data.status === 'PAID'"
              @click="voidIt"
            >
              {{ voiding ? 'Anulando…' : 'Anular' }}
            </button>
          </template>
          <span>
            Lo conserva para auditar y devuelve sus trámites a pendientes de
            facturar
          </span>
        </v-tooltip>
        <v-tooltip top>
          <template slot="activator">
            <button
              class="sd__btn sd__btn--ghost"
              :disabled="uploading || isVoid"
              @click="pick"
            >
              <v-icon small class="sd__btn-icon">attach_file</v-icon>
              {{ uploading ? 'Subiendo…' : 'Adjuntar' }}
            </button>
          </template>
          <span>Sube la factura, el recibo o cualquier soporte</span>
        </v-tooltip>
        <v-tooltip top>
          <template slot="activator">
            <button
              class="sd__btn sd__btn--send"
              :disabled="sending || isVoid"
              @click="send"
            >
              <v-icon small class="sd__btn-icon">mail</v-icon>
              {{ sending ? 'Enviando…' : data.sentAt ? 'Reenviar' : 'Enviar' }}
            </button>
          </template>
          <span>
            Le llega el detalle completo, el cuadro de KPI y el enlace de
            descarga
          </span>
        </v-tooltip>
        <v-tooltip top>
          <template slot="activator">
            <button
              class="sd__btn sd__btn--pay"
              :disabled="isVoid || data.status === 'PAID'"
              @click="paying = !paying"
            >
              <v-icon small class="sd__btn-icon">check_circle</v-icon>
              Registrar pago
            </button>
          </template>
          <span>
            {{
              data.status === 'PAID'
                ? 'Ya está pagado por completo'
                : 'Anota lo que el cliente canceló; cada abono se guarda aparte'
            }}
          </span>
        </v-tooltip>
      </template>
      <button
        v-else
        class="sd__btn sd__btn--ghost"
        @click="$emit('close-statement')"
      >
        Cerrar
      </button>
    </footer>

    <!-- Registro del pago: se abre en el pie para no tapar el detalle -->
    <div v-if="paying && !isVoid" class="sd__pay">
      <!-- Se registra UN abono: el monto es el de este pago, no el total
           acumulado. Vacío = el saldo completo, que es el caso normal. -->
      <div class="sd__pay-field">
        <label class="sd__label">Monto de este abono (B/.)</label>
        <input
          v-model="pay.amount"
          type="text"
          inputmode="decimal"
          class="sd__input"
          :placeholder="money(saldo)"
        />
        <span class="sd__pay-hint">
          Saldo por cobrar B/. {{ money(saldo) }}. Déjalo vacío si canceló todo.
        </span>
      </div>
      <div class="sd__pay-field">
        <label class="sd__label">Fecha del pago</label>
        <input v-model="pay.date" type="date" class="sd__input" />
      </div>
      <button class="sd__btn sd__btn--ghost" @click="paying = false">
        Cancelar
      </button>
      <button class="sd__btn sd__btn--pay" :disabled="saving" @click="savePay">
        {{ saving ? 'Guardando…' : 'Guardar abono' }}
      </button>
    </div>
  </v-card>
</template>

<script>
import moment from 'moment'
import SessionHelper from '@/helpers/SessionHelper'
import { getFullPath } from '@/helpers/TransitHelper'

// El cliente lee su estado de cuenta; emitir, cobrar y tocar los archivos es
// de Transit. Se listan los roles de cliente en vez de los de Transit: si
// mañana se agrega un rol interno, hereda los permisos correctos solo.
const CLIENT_ROLES = ['LAB', 'DIST', 'CLI']

// Nombre de cada tipo si el backend no manda kindLabel.
const KIND_TITLE = {
  REEMBOLSO: 'Reembolso de gastos operativos',
  SERVICIOS: 'Servicios aduanales',
}

// Sin depender del locale de moment: la app no lo fija.
const MESES = [
  'Enero',
  'Febrero',
  'Marzo',
  'Abril',
  'Mayo',
  'Junio',
  'Julio',
  'Agosto',
  'Septiembre',
  'Octubre',
  'Noviembre',
  'Diciembre',
]

export default {
  props: {
    statementId: {
      type: Number,
      required: true,
    },
  },
  data: () => ({
    data: {},
    loading: true,
    uploading: false,
    sending: false,
    voiding: false,
    saving: false,
    paying: false,
    notifyClient: true,
    downloading: false,
    error: '',
    pay: { amount: '', date: '' },
    // Edición en línea del # Control, por trámite:
    // { [invoiceId]: { draft, saving, error } }.
    control: {},
  }),
  computed: {
    // Historial de abonos del backend; vacío mientras carga o si no hay.
    payments() {
      return (this.data && this.data.payments) || []
    },
    canEdit() {
      return CLIENT_ROLES.indexOf(SessionHelper.role) === -1
    },
    // Formato anterior: los emitidos antes de separarlos por tipo (quedaron
    // anulados). No tienen mes y la migración les dejó kind REEMBOLSO, pero
    // mezclaban los tres conceptos.
    isLegacy() {
      if (!this.data.id) return false
      return !!this.data.legacy || !this.data.period
    },
    isVoid() {
      return this.data.status === 'VOID'
    },
    eyebrow() {
      if (!this.data.id) return 'Estado de cuenta'
      if (this.isLegacy) return 'Estado de cuenta (formato anterior)'
      const tipo = this.data.kindLabel || KIND_TITLE[this.data.kind]
      return tipo ? `Estado de cuenta · ${tipo}` : 'Estado de cuenta'
    },
    // Qué conceptos se muestran: los del tipo. Uno del formato anterior
    // muestra los tres, o sus columnas no sumarían el total.
    showTax() {
      return this.isLegacy || this.data.kind !== 'SERVICIOS'
    },
    showReimbursement() {
      return this.isLegacy || this.data.kind !== 'SERVICIOS'
    },
    showService() {
      return this.isLegacy || this.data.kind === 'SERVICIOS'
    },
    // Al anular se agrega el motivo con " | " aunque no hubiera nota: el
    // separador suelto no es parte del texto.
    notes() {
      return String(this.data.notes || '')
        .replace(/^\s*\|\s*/, '')
        .trim()
    },
    saldo() {
      return this.round((this.data.total || 0) - (this.data.paidAmount || 0))
    },
    antiguedad() {
      if (!this.data.kpi || !this.data.kpi.antiguedad) return []
      return this.data.kpi.antiguedad.filter(b => b.n > 0)
    },
    badgeText() {
      const map = {
        PAID: 'Pagado',
        PARTIAL: 'Abonado',
        VOID: 'Anulado',
        ISSUED: 'Por cobrar',
      }
      return map[this.data.status] || '—'
    },
    badgeClass() {
      return {
        'sd__badge--green': this.data.status === 'PAID',
        'sd__badge--amber': this.data.status === 'PARTIAL',
        'sd__badge--grey': this.data.status === 'VOID',
        'sd__badge--red': this.data.status === 'ISSUED',
      }
    },
  },
  created() {
    this.pay.date = moment().format('YYYY-MM-DD')
    this.load()
  },
  methods: {
    round(n) {
      return Math.round((n + Number.EPSILON) * 100) / 100
    },
    money(value) {
      const n = parseFloat(value)
      if (Number.isNaN(n)) return '0.00'
      return n.toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })
    },
    dateOf(value) {
      return value ? moment(value).format('DD/MM/YYYY') : '—'
    },
    dateTimeOf(value) {
      return value ? moment(value).format('DD/MM/YYYY HH:mm') : '—'
    },
    nullable(value) {
      return value === null || typeof value === 'undefined' ? '—' : value
    },
    // "Septiembre 2026" a partir de AAAA-MM.
    mesDe(period) {
      const m = /^(\d{4})-(\d{2})$/.exec(period || '')
      if (!m) return ''
      return `${MESES[parseInt(m[2], 10) - 1]} ${m[1]}`
    },
    conceptOf(invoice, concept) {
      const found = (invoice.conceptos || []).find(c => c.concept === concept)
      return found ? this.money(found.amount) : '—'
    },
    // Los números de factura del cliente. Si la API es la anterior y no los
    // manda, se arman con las facturas hijas del consolidado.
    numerosDe(inv) {
      if (inv.clientInvoiceNumbers && inv.clientInvoiceNumbers.length) {
        return inv.clientInvoiceNumbers
      }
      const hijas = (inv.facturas || []).map(f => f.invoiceNumber)
      return inv.consolidated && hijas.length ? hijas : [inv.invoiceNumber]
    },
    // El consolidado se nombra por su CONS- (internalNumber del padre). El
    // control F&D viejo (LN…) ya no es su número: va aparte, como en las
    // bandas de Transit (mixin TramiteBand).
    consolidadoDe(inv) {
      return (
        inv.internalNumber ||
        inv.consolidationNumber ||
        inv.fdcontrolNumber ||
        inv.invoiceNumber
      )
    },
    controlFdDe(inv) {
      if (!inv.consolidated || !inv.fdcontrolNumber) return null
      return inv.fdcontrolNumber === this.consolidadoDe(inv)
        ? null
        : inv.fdcontrolNumber
    },
    // ---- # Control (número de declaración), editable en la fila ----
    controlOf(invoiceId) {
      return (
        this.control[invoiceId] || { draft: null, saving: false, error: '' }
      )
    },
    controlValue(inv) {
      const st = this.control[inv.invoiceId]
      return st && st.draft !== null ? st.draft : inv.controlNumber || ''
    },
    onControlInput(inv, value) {
      this.$set(this.control, inv.invoiceId, {
        ...this.controlOf(inv.invoiceId),
        draft: value,
        error: '',
      })
    },
    // Escape descarta lo escrito y suelta el foco; el blur que sigue no
    // guarda nada porque ya no hay borrador.
    cancelControl(inv, event) {
      this.$set(this.control, inv.invoiceId, {
        draft: null,
        saving: false,
        error: '',
      })
      event.target.value = inv.controlNumber || ''
      event.target.blur()
    },
    // inv.invoiceId es el id del TRÁMITE (los ítems del estado se guardan por
    // trámite y el # Control se lee del trámite vivo, no de una foto).
    saveControl(inv) {
      const st = this.controlOf(inv.invoiceId)
      if (st.draft === null || st.saving) return
      const next = String(st.draft).trim()
      const current = inv.controlNumber || ''
      if (next === current) {
        this.$set(this.control, inv.invoiceId, {
          draft: null,
          saving: false,
          error: '',
        })
        return
      }
      this.$set(this.control, inv.invoiceId, {
        draft: next,
        saving: true,
        error: '',
      })
      this.$store
        .dispatch('invoices/updateAccounting', {
          invoiceId: inv.invoiceId,
          controlNumber: next,
        })
        .then(response => {
          if (!response || (response.status && response.status >= 400)) {
            this.$set(this.control, inv.invoiceId, {
              draft: next,
              saving: false,
              error: this.errorText(
                response,
                'No se pudo guardar el # Control.'
              ),
            })
            return
          }
          this.$set(inv, 'controlNumber', next === '' ? null : next)
          this.$set(this.control, inv.invoiceId, {
            draft: null,
            saving: false,
            error: '',
          })
        })
        .catch(() => {
          this.$set(this.control, inv.invoiceId, {
            draft: next,
            saving: false,
            error: 'No se pudo guardar el # Control.',
          })
        })
    },
    // El backend responde { error: 'mensaje' }; se cubre también la forma
    // anidada por si cambia.
    errorText(response, fallback) {
      const data = response && response.data
      const err = data && data.error
      if (typeof err === 'string' && err) return err
      if (err && typeof err.error === 'string' && err.error) return err.error
      return fallback
    },
    // Descarga solo este estado de cuenta: el mismo zip que la lista (Excel
    // con detalle y KPI más los adjuntos), filtrado por su id. El layout abre
    // el archivo cuando el backend lo deja listo.
    download() {
      this.error = ''
      this.downloading = true
      this.$socket.emit('exportAccountStatements', {
        id: this.statementId,
        token: SessionHelper.token,
      })
    },
    fileUrl: getFullPath,
    load() {
      this.loading = true
      this.$store
        .dispatch('accountStatement/getOne', { id: this.statementId })
        .then(response => {
          if (!response || (response.status && response.status >= 400)) {
            this.error = 'No se pudo cargar el estado de cuenta.'
            return
          }
          this.data = response
        })
        .finally(() => {
          this.loading = false
        })
    },
    pick() {
      this.$refs.file.click()
    },
    upload(e) {
      const files = Array.from(e.target.files || [])
      e.target.value = ''
      if (!files.length) return
      this.error = ''
      this.uploading = true
      this.$store
        .dispatch('accountStatement/uploadDocuments', {
          id: this.statementId,
          files,
          sendEmails: this.notifyClient ? 'true' : 'false',
        })
        .then(response => {
          if (!response || (response.status && response.status >= 400)) {
            this.error = this.errorText(
              response,
              'No se pudieron subir los documentos.'
            )
            return
          }
          this.load()
          this.$emit('changed')
        })
        .finally(() => {
          this.uploading = false
        })
    },
    removeDoc(doc) {
      this.$store
        .dispatch('accountStatement/deleteDocument', { documentId: doc.id })
        .then(response => {
          if (!response || (response.status && response.status >= 400)) {
            this.error = 'No se pudo quitar el documento.'
            return
          }
          this.load()
          this.$emit('changed')
        })
    },
    send() {
      this.error = ''
      this.sending = true
      this.$store
        .dispatch('accountStatement/send', { id: this.statementId })
        .then(response => {
          if (!response || (response.status && response.status >= 400)) {
            this.error =
              (response && response.data && response.data.error) ||
              'No se pudo enviar el estado de cuenta.'
            return
          }
          this.load()
          this.$emit('changed')
        })
        .finally(() => {
          this.sending = false
        })
    },
    savePay() {
      const amount = parseFloat(this.pay.amount)
      if (this.pay.amount !== '' && (Number.isNaN(amount) || amount <= 0)) {
        this.error = 'El monto del abono debe ser un número mayor que cero.'
        return
      }
      this.error = ''
      this.saving = true
      this.$store
        .dispatch('accountStatement/pay', {
          id: this.statementId,
          paidAmount: this.pay.amount === '' ? undefined : this.pay.amount,
          paidAt: this.pay.date || undefined,
        })
        .then(response => {
          if (!response || (response.status && response.status >= 400)) {
            this.error =
              (response && response.data && response.data.error) ||
              'No se pudo registrar el abono.'
            return
          }
          this.paying = false
          // El formulario se vacía: si se reabre para otro abono, el monto
          // anterior no debe quedar escrito y guardarse dos veces.
          this.pay = { amount: '', date: '' }
          this.load()
          this.$emit('changed')
        })
        .finally(() => {
          this.saving = false
        })
    },
    // Deshacer un abono mal registrado. El backend recalcula el acumulado y
    // el estado: si no queda ninguno, el estado vuelve a "Por cobrar".
    removePayment(ab) {
      this.error = ''
      this.saving = true
      this.$store
        .dispatch('accountStatement/deletePayment', { paymentId: ab.id })
        .then(response => {
          if (!response || (response.status && response.status >= 400)) {
            this.error =
              (response && response.data && response.data.error) ||
              'No se pudo eliminar el abono.'
            return
          }
          this.load()
          this.$emit('changed')
        })
        .finally(() => {
          this.saving = false
        })
    },
    voidIt() {
      this.error = ''
      this.voiding = true
      this.$store
        .dispatch('accountStatement/voidStatement', {
          id: this.statementId,
          reason: 'Anulado desde Contabilidad',
        })
        .then(response => {
          if (!response || (response.status && response.status >= 400)) {
            this.error =
              (response && response.data && response.data.error) ||
              'No se pudo anular el estado de cuenta.'
            return
          }
          this.paying = false
          this.load()
          this.$emit('changed')
        })
        .finally(() => {
          this.voiding = false
        })
    },
  },
  sockets: {
    accountStatementsFile() {
      this.downloading = false
    },
    accountStatementsFileError(message) {
      this.downloading = false
      this.error = message || 'No se pudo generar la descarga.'
    },
  },
}
</script>

<style scoped>
.sd {
  border-radius: 10px;
  overflow: hidden;
}

.sd__head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  padding: 18px 22px;
  background: #254f87;
}
.sd__head-right {
  display: flex;
  align-items: center;
  gap: 10px;
  flex: none;
}
.sd__eyebrow {
  margin: 0;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #a8c4e6;
}
.sd__title {
  margin: 2px 0 0;
  font-size: 22px;
  font-weight: 700;
  color: #ffffff;
  line-height: 1.2;
}
.sd__scope {
  margin: 6px 0 0;
  font-size: 13px;
  color: #dbe7f5;
}
.sd__badge {
  padding: 4px 10px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  white-space: nowrap;
}
.sd__badge--red {
  background: #fee2e2;
  color: #991b1b;
}
.sd__badge--amber {
  background: #fef3c7;
  color: #92400e;
}
.sd__badge--green {
  background: #dcfce7;
  color: #14532d;
}
.sd__badge--grey {
  background: #e2e8f0;
  color: #475569;
}
.sd__close {
  flex: none;
  width: 28px;
  height: 28px;
  border: 0;
  border-radius: 6px;
  background: rgba(255, 255, 255, 0.14);
  cursor: pointer;
}
.sd__close .v-icon {
  color: #ffffff !important;
  font-size: 16px !important;
}
.sd__close:hover {
  background: rgba(255, 255, 255, 0.26);
}

.sd__body {
  padding: 20px 22px;
  max-height: 60vh;
  overflow-y: auto;
  /* Lo ancho se desplaza dentro de su tabla, nunca el diálogo entero. */
  overflow-x: hidden;
}

.sd__totals {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-bottom: 20px;
}
.sd__total {
  flex: 1 1 150px;
  padding: 10px 12px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  background: #f8fafc;
}
.sd__total--grand {
  border-color: #254f87;
  background: #eef2f7;
}
.sd__total-label {
  display: block;
  font-size: 11px;
  font-weight: 600;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}
.sd__total-value {
  display: block;
  margin-top: 4px;
  font-size: 17px;
  color: #1e293b;
}
.sd__total--grand .sd__total-value {
  color: #254f87;
}
.sd__total-hint {
  display: block;
  margin-top: 2px;
  font-size: 11px;
  color: #94a3b8;
}
.sd__total-note {
  display: block;
  margin-top: 2px;
  font-size: 11px;
  color: #92400e;
  font-weight: 600;
}

.sd__section {
  margin-bottom: 22px;
}
.sd__section-title {
  margin: 0 0 10px;
  font-size: 13px;
  font-weight: 700;
  color: #1e293b;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}
.sd__section-note {
  margin-left: 8px;
  font-size: 11px;
  font-weight: 500;
  color: #94a3b8;
  text-transform: none;
  letter-spacing: 0;
}

.sd__kpis {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 12px;
}
.sd__kpi {
  flex: 1 1 130px;
  padding: 9px 11px;
  border: 1px solid #e2e8f0;
  border-radius: 7px;
}
.sd__kpi-label {
  display: block;
  font-size: 11px;
  color: #64748b;
}
.sd__kpi-value {
  display: block;
  margin-top: 3px;
  font-size: 16px;
  color: #1e293b;
}

.sd__table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
}
/* La tabla de líneas se desplaza a lo ancho si no cabe. La barra se dibuja
   siempre (en Mac se esconde sola y la tabla parecía cortada). */
.sd__table-wrap {
  width: 100%;
  overflow-x: auto;
  scrollbar-width: thin;
  scrollbar-color: #cbd5e1 #f1f5f9;
}
.sd__table-wrap::-webkit-scrollbar {
  height: 8px;
}
.sd__table-wrap::-webkit-scrollbar-track {
  background: #f1f5f9;
  border-radius: 4px;
}
.sd__table-wrap::-webkit-scrollbar-thumb {
  background: #cbd5e1;
  border-radius: 4px;
}
.sd__table-wrap::-webkit-scrollbar-thumb:hover {
  background: #94a3b8;
}
/* Bordes separados: con collapse, el borde de una celda fija se queda atrás
   al desplazar. */
.sd__table--lines {
  border-collapse: separate;
  border-spacing: 0;
}
.sd__col-factura {
  min-width: 150px;
}
/* El Subtotal queda fijo a la derecha, con fondo propio y una sombra suave
   que marca lo que pasa por debajo. */
.sd__sticky {
  position: sticky;
  right: 0;
  z-index: 1;
}
.sd__table th.sd__sticky {
  background: #ffffff;
}
.sd__sticky::before {
  content: '';
  position: absolute;
  top: 0;
  bottom: 0;
  left: -8px;
  width: 8px;
  background: linear-gradient(
    to left,
    rgba(15, 23, 42, 0.07),
    rgba(15, 23, 42, 0)
  );
  pointer-events: none;
}
.sd__table--aging {
  max-width: 460px;
}
.sd__table th {
  padding: 8px 10px;
  border-bottom: 2px solid #e2e8f0;
  color: #475569;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  text-align: left;
  white-space: nowrap;
}
.sd__table td {
  padding: 8px 10px;
  border-bottom: 1px solid #f1f5f9;
  color: #334155;
}
.sd__right {
  text-align: right;
}
.sd__nowrap {
  white-space: nowrap;
}
.sd__strong {
  font-weight: 600;
  color: #1e293b;
}
.sd__amount {
  font-weight: 700;
  color: #1e293b;
  background: #f8fafc;
}

.sd__pay-amount {
  font-weight: 700;
  color: #0f172a;
  font-variant-numeric: tabular-nums;
}
/* La ayuda del abono: el saldo a la vista para no tener que calcularlo. */
.sd__pay-hint {
  display: block;
  margin-top: 4px;
  font-size: 11.5px;
  color: #64748b;
}

.sd__docs {
  margin: 0;
  padding: 0;
  list-style: none;
}
.sd__doc {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 0;
  border-bottom: 1px solid #f1f5f9;
  font-size: 13px;
}
.sd__doc-icon {
  color: #64748b !important;
  font-size: 16px !important;
}
.sd__doc-name {
  flex: 1;
  min-width: 0;
  color: #254f87;
  text-decoration: none;
  word-break: break-all;
}
.sd__doc-name:hover {
  text-decoration: underline;
}
.sd__doc-date {
  flex: none;
  color: #94a3b8;
  font-size: 12px;
}
.sd__doc-del {
  flex: none;
  width: 26px;
  height: 26px;
  border: 0;
  border-radius: 6px;
  background: transparent;
  cursor: pointer;
}
.sd__doc-del .v-icon {
  color: #94a3b8 !important;
  font-size: 16px !important;
}
.sd__doc-del:hover {
  background: #fee2e2;
}
.sd__doc-del:hover .v-icon {
  color: #dc2626 !important;
}

.sd__help {
  margin: 0 0 8px;
  font-size: 12px;
  color: #64748b;
  line-height: 1.5;
}
.sd__notes {
  margin: 0 0 8px;
  padding: 10px 12px;
  border-left: 3px solid #cbd5e1;
  background: #f8fafc;
  color: #475569;
  font-size: 13px;
  white-space: pre-line;
}
.sd__error {
  margin: 8px 0 0;
  padding: 10px 12px;
  border-radius: 6px;
  background: rgba(220, 38, 38, 0.1);
  color: #991b1b;
  font-size: 13px;
}

.sd__foot {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 10px;
  padding: 14px 22px;
  border-top: 1px solid #e2e8f0;
  background: #f8fafc;
  flex-wrap: wrap;
}
.sd__notify {
  margin-right: auto;
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: #475569;
  cursor: pointer;
}
.sd__notify--off {
  opacity: 0.55;
  cursor: not-allowed;
}

.sd__pay {
  display: flex;
  align-items: flex-end;
  gap: 10px;
  padding: 14px 22px;
  border-top: 1px solid #e2e8f0;
  background: #eef2f7;
  flex-wrap: wrap;
}
.sd__pay-field {
  flex: 1 1 140px;
  min-width: 0;
}
.sd__label {
  display: block;
  margin-bottom: 5px;
  font-size: 12px;
  font-weight: 600;
  color: #475569;
}
.sd__input {
  width: 100%;
  height: 34px;
  padding: 0 10px;
  border: 1px solid #cbd5e1;
  border-radius: 6px;
  background: #ffffff;
  color: #1e293b;
  font-size: 13px;
  outline: none;
}
.sd__input:focus {
  border-color: #254f87;
  box-shadow: 0 0 0 3px rgba(37, 79, 135, 0.14);
}

.sd__btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 34px;
  padding: 0 14px;
  border-radius: 6px;
  border: 1px solid transparent;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.18s ease;
}
.sd__btn:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}
.sd__btn-icon {
  font-size: 16px !important;
}
.sd__btn--ghost {
  border-color: #cbd5e1;
  background: #ffffff;
  color: #475569;
}
.sd__btn--ghost .v-icon {
  color: #64748b !important;
}
.sd__btn--ghost:hover:not(:disabled) {
  background: #f1f5f9;
}
.sd__btn--send {
  background: #254f87;
  color: #ffffff;
}
.sd__btn--send .v-icon {
  color: #ffffff !important;
}
.sd__btn--send:hover:not(:disabled) {
  background: #1e4270;
}
.sd__btn--pay {
  background: #15803d;
  color: #ffffff;
}
.sd__btn--pay .v-icon {
  color: #ffffff !important;
}
.sd__btn--pay:hover:not(:disabled) {
  background: #126b33;
}
.sd__factura {
  line-height: 1.35;
  white-space: nowrap;
}
.sd__facturas {
  margin-top: 2px;
  font-size: 11px;
  font-weight: 400;
  color: #64748b;
  /* Con el control F&D la referencia se alarga: baja de línea en vez de
     ensanchar la columna. */
  white-space: normal;
}
/* # Control editable en la fila. */
.sd__control {
  vertical-align: top;
}
.sd__control-input {
  width: 120px;
  height: 30px;
  padding: 0 8px;
  font-variant-numeric: tabular-nums;
}
.sd__control-input--error {
  border-color: #dc2626;
}
.sd__control-status,
.sd__control-error {
  display: block;
  margin-top: 3px;
  max-width: 140px;
  font-size: 11px;
  line-height: 1.3;
  white-space: normal;
}
.sd__control-status {
  color: #64748b;
}
.sd__control-error {
  color: #b91c1c;
}
.sd__help--control {
  margin: 8px 0 0;
}
</style>
